import React, { useEffect, useMemo, useState } from "react";
import { growthApi } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Search, Save, ChevronDown, ChevronUp, ShieldAlert } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const tierColor = (tier) => {
  if (tier?.startsWith("TIER 1")) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (tier?.startsWith("TIER 2")) return "bg-blue-100 text-blue-700 border-blue-200";
  if (tier?.startsWith("TIER 3")) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-500 border-slate-200";
};

const AVAILABILITY_OPTIONS = [
  "Now accepting referral inquiries",
  "Expanding service availability",
  "Growing provider availability",
  "Contact us about service availability in this county.",
];

const FILTERS = [
  { key: "all", label: "All counties" },
  { key: "indexable", label: "Indexable pages" },
  { key: "unknown", label: "Provider count UNKNOWN" },
  { key: "recruit", label: "Recruitment priority" },
  { key: "hpc", label: "HPC demand" },
  { key: "nmt", label: "NMT demand" },
  { key: "respite", label: "Respite demand" },
  { key: "shared", label: "Shared Living demand" },
  { key: "pdhpc", label: "Participant-Directed HPC" },
  { key: "private", label: "Private Pay demand" },
];

const NumField = ({ label, value, onChange, placeholder }) => (
  <div>
    <Label className="text-[11px]">{label}</Label>
    <Input type="number" value={value ?? ""} placeholder={placeholder || "UNKNOWN"} className="mt-1 h-8 text-sm"
      onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))} />
  </div>
);

const CountyRow = ({ c, onSaved }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(c);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setEdit((e) => ({ ...e, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        providerCount: edit.providerCount, agencyProviderCount: edit.agencyProviderCount,
        serviceProviderCount: edit.serviceProviderCount, doddReferralActivity: edit.doddReferralActivity,
        openRfpCount: edit.openRfpCount, transportationDemand: edit.transportationDemand,
        hpcDemand: edit.hpcDemand, respiteDemand: edit.respiteDemand, nmtDemand: edit.nmtDemand,
        sharedLivingDemand: edit.sharedLivingDemand, pdHpcDemand: edit.pdHpcDemand,
        privatePayDemand: edit.privatePayDemand, staffingCapacity: edit.staffingCapacity,
        referralRelationships: edit.referralRelationships, dataSources: edit.dataSources,
        lastDataRefresh: edit.lastDataRefresh, pageApproved: edit.pageApproved,
        availabilityStatus: edit.availabilityStatus, intro: edit.intro, boardName: edit.boardName,
        notes: edit.notes,
        manualScoreOverride: edit.manualScoreOverride,
        clearOverride: edit.manualScoreOverride == null ? true : null,
      };
      const clean = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null || true));
      const res = await growthApi.updateCounty(c.id, clean);
      onSaved(res.data);
      setEdit(res.data);
      toast({ title: `${c.name} County saved`, description: `Score: ${res.data.opportunityScore} · ${res.data.tier}` });
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`county-row-${c.slug}`} className="bg-white rounded-xl border border-slate-100 shadow-sm">
      <button className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left" onClick={() => setOpen((o) => !o)}>
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <span className="font-semibold text-[#0E2A33]">{c.name}</span>
          <span className="text-xs text-slate-400">{c.population.toLocaleString()} pop</span>
          <Badge className={`border text-[11px] ${tierColor(c.tier)}`}>{c.opportunityScore} · {c.tier}</Badge>
          {c.providerCountDisplay === "UNKNOWN"
            ? <Badge className="border text-[11px] bg-slate-100 text-slate-500 border-slate-200">Providers: UNKNOWN</Badge>
            : <Badge className="border text-[11px] bg-teal-50 text-teal-700 border-teal-200">Providers: {c.providerCountDisplay}{c.providerDensity != null ? ` (${c.providerDensity}/10k)` : ""}</Badge>}
          {c.pageApproved
            ? <Badge className="border text-[11px] bg-emerald-100 text-emerald-700 border-emerald-200">Page: INDEXABLE</Badge>
            : <Badge className="border text-[11px] bg-amber-50 text-amber-700 border-amber-200">Page: NOINDEX</Badge>}
          {c.recruitmentPriority && <Badge className="border text-[11px] bg-red-100 text-red-700 border-red-200">RECRUITMENT PRIORITY</Badge>}
          {c.manualScoreOverride != null && <Badge className="border text-[11px] bg-violet-100 text-violet-700 border-violet-200">Manual override</Badge>}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <NumField label="Provider Count (verified)" value={edit.providerCount} onChange={set("providerCount")} />
            <NumField label="Agency Providers" value={edit.agencyProviderCount} onChange={set("agencyProviderCount")} />
            <NumField label="Relevant Service Providers" value={edit.serviceProviderCount} onChange={set("serviceProviderCount")} />
            <NumField label="DODD Referral Activity" value={edit.doddReferralActivity} onChange={set("doddReferralActivity")} placeholder="0" />
            <NumField label="Open RFP / Referral Count" value={edit.openRfpCount} onChange={set("openRfpCount")} placeholder="0" />
            <NumField label="HPC Demand (0-10)" value={edit.hpcDemand} onChange={set("hpcDemand")} placeholder="0" />
            <NumField label="Respite Demand (0-10)" value={edit.respiteDemand} onChange={set("respiteDemand")} placeholder="0" />
            <NumField label="Transportation Demand (0-10)" value={edit.transportationDemand} onChange={set("transportationDemand")} placeholder="0" />
            <NumField label="NMT Demand (0-10)" value={edit.nmtDemand} onChange={set("nmtDemand")} placeholder="0" />
            <NumField label="Shared Living Demand (0-10)" value={edit.sharedLivingDemand} onChange={set("sharedLivingDemand")} placeholder="0" />
            <NumField label="PD HPC Demand (0-10)" value={edit.pdHpcDemand} onChange={set("pdHpcDemand")} placeholder="0" />
            <NumField label="Private Pay Demand (0-10)" value={edit.privatePayDemand} onChange={set("privatePayDemand")} placeholder="0" />
            <NumField label="Staffing Capacity (0-10)" value={edit.staffingCapacity} onChange={set("staffingCapacity")} placeholder="0" />
            <NumField label="Referral Relationships (0-10)" value={edit.referralRelationships} onChange={set("referralRelationships")} placeholder="0" />
            <NumField label="Manual Score Override (0-100)" value={edit.manualScoreOverride} onChange={set("manualScoreOverride")} placeholder="Auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            <div>
              <Label className="text-[11px]">Data Sources</Label>
              <Input value={edit.dataSources || ""} onChange={(e) => set("dataSources")(e.target.value)} className="mt-1 h-8 text-sm" placeholder="DODD Find a Provider, county board, etc." />
            </div>
            <div>
              <Label className="text-[11px]">Last Data Refresh</Label>
              <Input type="date" value={edit.lastDataRefresh || ""} onChange={(e) => set("lastDataRefresh")(e.target.value)} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-[11px]">Public Availability Status</Label>
              <Select value={edit.availabilityStatus} onValueChange={set("availabilityStatus")}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{AVAILABILITY_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-[11px]">County Board Name (public)</Label>
              <Input value={edit.boardName || ""} onChange={(e) => set("boardName")(e.target.value)} className="mt-1 h-8 text-sm" placeholder="e.g. Marion County Board of DD" />
            </div>
            <div className="flex items-end gap-3 pb-1">
              <div className="flex items-center gap-2">
                <Switch data-testid={`county-index-switch-${c.slug}`} checked={!!edit.pageApproved} onCheckedChange={set("pageApproved")} />
                <span className="text-sm text-slate-600">Page indexable (requires unique, verified content)</span>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-[11px]">County Page Intro (public — unique content required for indexing)</Label>
            <Textarea value={edit.intro || ""} onChange={(e) => set("intro")(e.target.value)} className="mt-1 text-sm" rows={3} placeholder="Unique, verified county-specific introduction..." />
          </div>
          <div className="mt-3">
            <Label className="text-[11px]">Internal Notes</Label>
            <Textarea value={edit.notes || ""} onChange={(e) => set("notes")(e.target.value)} className="mt-1 text-sm" rows={2} />
          </div>
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <a href={`/ohio/${c.slug}/home-care`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E5A72] hover:underline">
              View public page: /ohio/{c.slug}/home-care →
            </a>
            <Button data-testid={`county-save-${c.slug}`} size="sm" disabled={busy} onClick={save} className="bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full px-5">
              <Save className="mr-2 h-4 w-4" /> Save county
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const CountyEngine = () => {
  const [counties, setCounties] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    growthApi.counties().then((r) => setCounties(r.data)).finally(() => setLoading(false));
  }, []);

  const onSaved = (updated) => setCounties((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  const filtered = useMemo(() => {
    let list = [...counties].sort((a, b) => b.opportunityScore - a.opportunityScore || a.name.localeCompare(b.name));
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
    const f = {
      indexable: (c) => c.pageApproved,
      unknown: (c) => c.providerCountDisplay === "UNKNOWN",
      recruit: (c) => c.recruitmentPriority,
      hpc: (c) => c.hpcDemand > 0,
      nmt: (c) => c.nmtDemand > 0,
      respite: (c) => c.respiteDemand > 0,
      shared: (c) => c.sharedLivingDemand > 0,
      pdhpc: (c) => c.pdHpcDemand > 0,
      private: (c) => c.privatePayDemand > 0,
    }[filter];
    return f ? list.filter(f) : list;
  }, [counties, q, filter]);

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">Data rules:</span> Provider counts stay <b>UNKNOWN</b> until you verify them
          from official sources (DODD Find a Provider, county boards, AAAs) — UNKNOWN is never treated as zero.
          County pages remain <b>NOINDEX</b> until you approve them with unique, verified content. Never label a county
          "underserved" publicly based on this score.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="county-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search county" className="pl-9 h-10 w-48" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="county-filter-select" className="h-10 w-64"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{filtered.length} of {counties.length} counties</span>
      </div>
      {loading && <div className="text-center text-slate-400 py-10">Loading Ohio Growth Map…</div>}
      <div className="space-y-2 max-h-[900px] overflow-y-auto pr-1">
        {filtered.map((c) => <CountyRow key={c.id} c={c} onSaved={onSaved} />)}
      </div>
    </div>
  );
};

export default CountyEngine;
